import { GoogleGenerativeAI } from "@google/generative-ai";
import { Book, SearchResult, BookCheckResult, Language } from "../types";

const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

if (!apiKey) {
  throw new Error("API Key not found. Please set VITE_GEMINI_API_KEY in .env.local file");
}

// ایجاد نمونه API با استفاده از کتابخانه صحیح
const genAI = new GoogleGenerativeAI(apiKey);

/**
 * Helper to extract JSON from response
 */
const extractJSON = (text: string): any => {
  const codeBlockMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
  if (codeBlockMatch && codeBlockMatch[1]) {
    try {
      return JSON.parse(codeBlockMatch[1]);
    } catch (e) {}
  }

  const arrayMatch = text.match(/\[\s*\{[\s\S]*\}\s*\]/);
  if (arrayMatch && arrayMatch[0]) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch (e) {}
  }

  const objectMatch = text.match(/\{[\s\S]*\}/);
  if (objectMatch && objectMatch[0]) {
    try {
      return JSON.parse(objectMatch[0]);
    } catch (e) {}
  }

  throw new Error("Could not extract valid JSON from response.");
};

export const findBooksByGenre = async (genre: string, language: Language): Promise<SearchResult> => {
  try {
    const languageInstruction = language === 'fa' 
      ? "به زبان فارسی" 
      : "in English";

    const prompt = `You are a book expert. Based on your knowledge, list 6-8 popular New York Times Best Seller books in the "${genre}" genre from 2010-2025.

Return ONLY a JSON array (no markdown, no extra text):

[
  {
    "title": "Book Title",
    "author": "Author Name",
    "description": "Brief description ${languageInstruction}",
    "rating": 4.5,
    "publishedYear": "2020"
  }
]`;

    const model = genAI.getGenerativeModel({ model: "gemini-2.5-pro" });
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    let books: Book[] = [];
    try {
      books = extractJSON(text);
      if (!Array.isArray(books)) {
         // @ts-ignore
         if (books.books) books = books.books;
         // @ts-ignore
         else if (books.items) books = books.items;
         else throw new Error("Response was not an array");
      }
    } catch (e) {
      console.error("JSON Parse Error:", e);
      console.log("Raw Text:", text);
      throw new Error(language === 'fa' ? "خطا در دریافت لیست کتاب‌ها." : "Error parsing book list.");
    }

    return { books, sources: [] };

  } catch (error: any) {
    console.error("Error fetching books:", error);
    
    if (error?.message?.includes('429') || error?.message?.includes('quota')) {
      throw new Error(
        language === 'fa' 
          ? "سهمیه API تمام شده است. لطفاً 1 ساعت صبر کنید یا API Key جدید بسازید." 
          : "API quota exceeded. Please wait 1 hour or create a new API key."
      );
    }

    if (error?.message?.includes('API key not valid')) {
      throw new Error(
        language === 'fa'
          ? "API Key نامعتبر است. لطفاً از https://aistudio.google.com یک کلید جدید بسازید."
          : "Invalid API Key. Please create a new key from https://aistudio.google.com"
      );
    }
    
    throw new Error(
      language === 'fa'
        ? "خطا در ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید."
        : "Connection error. Please check your internet connection."
    );
  }
};

export const checkBookNYTStatus = async (query: string, language: Language): Promise<BookCheckResult> => {
  try {
    const languageInstruction = language === 'fa' 
      ? "پیام و توضیحات را به زبان فارسی بنویس." 
      : "Write message and description in English.";

    const prompt = `You are a book expert. Check if "${query}" (book or author) was on New York Times Best Seller lists (2010-2025).

${languageInstruction}

Return ONLY a JSON object (no markdown, no extra text):

{
  "isBestSeller": true/false,
  "books": [
    {
      "title": "Book Title",
      "author": "Author Name",
      "description": "Brief summary",
      "rating": 4.5,
      "publishedYear": "2020"
    }
  ],
  "message": "Clear explanation"
}`;

    const model = genAI.getGenerativeModel({ model: "gemini-2.5-pro" });
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    let resultData: any;
    try {
      resultData = extractJSON(text);
    } catch (e) {
      console.error("JSON Parse Error:", e);
      console.log("Raw Text:", text);
      throw new Error(language === 'fa' ? "خطا در بررسی کتاب." : "Error analyzing book.");
    }

    let books: Book[] = [];
    if (Array.isArray(resultData.books)) {
      books = resultData.books;
    } else if (resultData.book) {
      books = [resultData.book];
    }

    const result_obj: BookCheckResult = {
      isBestSeller: resultData.isBestSeller,
      books: books,
      message: resultData.message,
      sources: []
    };

    return result_obj;

  } catch (error: any) {
    console.error("Error checking book:", error);
    
    if (error?.message?.includes('429') || error?.message?.includes('quota')) {
      throw new Error(
        language === 'fa' 
          ? "سهمیه API تمام شده است. لطفاً 1 ساعت صبر کنید یا API Key جدید بسازید." 
          : "API quota exceeded. Please wait 1 hour or create a new API key."
      );
    }

    if (error?.message?.includes('API key not valid')) {
      throw new Error(
        language === 'fa'
          ? "API Key نامعتبر است. لطفاً از https://aistudio.google.com یک کلید جدید بسازید."
          : "Invalid API Key. Please create a new key from https://aistudio.google.com"
      );
    }
    
    throw new Error(
      language === 'fa'
        ? "خطا در ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید."
        : "Connection error. Please check your internet connection."
    );
  }
};
